import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import Toast from "@/components/feature/Toast";
import VideoPreview from "@/pages/detail/components/VideoPreview";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { dramas } from "@/mocks/dramas";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Detail() {
  const { id } = useParams();
  const drama = dramas.find((item) => item.id === id);
  const [activeChapter, setActiveChapter] = useState(drama?.chapters[0]?.index ?? 1);
  const [toast, setToast] = useState("");
  const { isFavorite, toggleFavorite } = useFavorites();
  const closeToast = useCallback(() => setToast(""), []);

  usePageMeta({
    title: drama
      ? `${drama.title} - 详细剧情介绍及角色分析 - 波多野结衣家庭教师`
      : "剧集未找到 - 波多野结衣家庭教师",
    description: drama
      ? `${drama.title}（${drama.year}，${drama.region}）剧情介绍、角色分析与章节目录。${drama.synopsis.slice(0, 70)}`
      : "该剧集可能已下架，返回分类页浏览其他热门电视剧的剧情介绍与角色分析。",
    keywords: drama
      ? `${drama.title},${drama.types.join(",")},剧情介绍,角色分析,${drama.director},${drama.cast.join(",")}`
      : "电视剧,剧情介绍",
  });

  useEffect(() => {
    setActiveChapter(drama?.chapters[0]?.index ?? 1);
  }, [id, drama]);

  const related = drama
    ? dramas
        .filter(
          (item) =>
            item.id !== drama.id &&
            (item.types.some((type) => drama.types.includes(type)) || item.director === drama.director),
        )
        .slice(0, 4)
    : [];

  const currentChapter =
    drama?.chapters.find((chapter) => chapter.index === activeChapter) ?? drama?.chapters[0];

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleFavorite = () => {
    if (!drama) return;
    const wasFavorite = isFavorite(drama.id);
    toggleFavorite(drama.id);
    setToast(wasFavorite ? `已取消收藏《${drama.title}》` : `已收藏《${drama.title}》，可在本机继续查看`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("链接已复制，可以粘贴到聊天窗口分享");
    } catch {
      setToast("当前浏览器不支持自动复制，请手动复制地址栏链接");
    }
  };

  const shareWeibo = () => {
    if (!drama) return;
    const share = `https://service.weibo.com/share/share.php?url=${encodeURIComponent(
      shareUrl,
    )}&title=${encodeURIComponent(`推荐一部剧《${drama.title}》：${drama.tagline}`)}`;
    window.open(share, "_blank", "noopener,noreferrer");
  };

  if (!drama) {
    return (
      <div className="min-h-screen bg-background-50">
        <SiteHeader />
        <main className="flex min-h-[70vh] items-center justify-center px-4 pt-[72px]">
          <div className="max-w-md text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-film-line text-2xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl text-foreground-950">没有找到这部剧</h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              剧集链接可能已失效，或者内容已经调整。你可以回到分类页看看其它正在热播的剧集。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category"
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                返回电视剧分类
              </Link>
              <Link
                to="/"
                className="cursor-pointer rounded-md border border-background-300/60 px-4 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
              >
                回到首页
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const favorited = isFavorite(drama.id);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="pt-[72px]">
        <section className="relative overflow-hidden border-b border-background-200/70">
          <div className="absolute inset-0">
            <img
              src={drama.cover}
              alt=""
              aria-hidden="true"
              className="h-full w-full scale-110 object-cover object-top opacity-20 blur-md"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/92 to-background-50/55"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60"></div>
          </div>

          <div className="relative mx-auto w-full max-w-[1240px] px-4 py-7 md:px-6 md:py-11">
            <nav
              aria-label="面包屑导航"
              className="flex flex-wrap items-center gap-1.5 font-label text-xs text-foreground-500"
            >
              <Link to="/" className="cursor-pointer transition-colors hover:text-accent-500">首页</Link>
              <i className="ri-arrow-right-s-line text-sm opacity-60"></i>
              <Link to="/category" className="cursor-pointer transition-colors hover:text-accent-500">电视剧分类</Link>
              <i className="ri-arrow-right-s-line text-sm opacity-60"></i>
              <span className="text-foreground-800">{drama.title}</span>
            </nav>

            <div className="mt-5 flex flex-col gap-6 md:flex-row md:gap-8">
              <div className="w-[150px] shrink-0 overflow-hidden rounded-lg border border-background-200/80 sm:w-[180px] md:w-[226px]">
                <div className="aspect-[2/3] w-full bg-background-200">
                  <img
                    src={drama.cover}
                    alt={`${drama.title} 高清剧集封面`}
                    title={`${drama.title} 剧情介绍与角色分析`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-accent-500/15 px-2.5 py-0.5 font-label text-[11px] text-accent-600">
                    {drama.status}
                  </span>
                  <span className="flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-0.5 font-label text-[11px] text-foreground-700">
                    <i className="ri-star-fill text-[10px] text-accent-500"></i>
                    {drama.rating.toFixed(1)} 分
                  </span>
                  <span className="rounded-full bg-background-100 px-2.5 py-0.5 font-label text-[11px] text-foreground-600">
                    热度 {drama.heat}
                  </span>
                </div>

                <h1 className="mt-3 font-heading text-3xl font-semibold leading-tight text-foreground-950 md:text-4xl">
                  {drama.title}
                </h1>
                <p className="mt-2 text-sm text-accent-600 md:text-[15px]">{drama.tagline}</p>

                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 font-label text-xs text-foreground-600">
                  <span>{drama.year}</span>
                  <span className="h-3 w-px bg-background-300/60"></span>
                  <span>{drama.region}</span>
                  <span className="h-3 w-px bg-background-300/60"></span>
                  <span>{drama.episodeCount} 集 · 每集 {drama.duration}</span>
                  <span className="h-3 w-px bg-background-300/60"></span>
                  <span>导演 {drama.director}</span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {drama.types.map((type) => (
                    <Link
                      key={type}
                      to={`/category?type=${encodeURIComponent(type)}`}
                      className="cursor-pointer rounded-full border border-background-300/60 px-2.5 py-0.5 font-label text-[11px] text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
                    >
                      {type}
                    </Link>
                  ))}
                </div>

                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  主演：{drama.cast.join(" / ")}
                </p>

                <div className="mt-6 flex flex-wrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={handleFavorite}
                    className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 font-label text-sm transition-colors ${
                      favorited
                        ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                        : "bg-primary-500 text-background-50 hover:bg-primary-600"
                    }`}
                  >
                    <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-base`}></i>
                    {favorited ? "已收藏" : "收藏这部剧"}
                  </button>
                  <button
                    type="button"
                    onClick={shareWeibo}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-3.5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                  >
                    <i className="ri-weibo-line text-base"></i>
                    微博
                  </button>
                  <button
                    type="button"
                    onClick={() => setToast("微信分享请在微信内打开本页，或先复制链接再发送给好友")}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-3.5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                  >
                    <i className="ri-wechat-line text-base"></i>
                    微信
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100 px-3.5 py-2.5 font-label text-sm text-foreground-800 transition-colors hover:border-accent-500/60 hover:text-accent-600"
                  >
                    <i className="ri-link" aria-hidden="true"></i>
                    复制链接
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="mx-auto grid w-full max-w-[1240px] gap-8 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-[1fr_318px]">
          <div className="min-w-0 space-y-10">
            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-book-open-line text-base"></i>
                </span>
                剧情简介
              </h2>
              <div className="mt-3 rounded-lg border border-background-200/70 bg-background-100/70 p-4 md:p-5">
                <p className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  {drama.synopsis}
                </p>
                <p className="mt-4 border-l-2 border-accent-500/60 pl-3 text-sm leading-relaxed text-foreground-600">
                  {drama.tagline}
                </p>
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-team-line text-base"></i>
                </span>
                角色与人物分析
              </h2>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                {drama.characters.map((character) => (
                  <div
                    key={character.name}
                    className="flex flex-col rounded-lg border border-background-200/70 bg-background-100 p-4 transition-colors hover:border-primary-500/40"
                  >
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500/12 font-heading text-sm text-primary-500">
                        {character.name.slice(0, 1)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-heading text-sm font-semibold text-foreground-950">
                          {character.name}
                        </p>
                        <p className="truncate font-label text-[11px] text-foreground-500">
                          {character.actor} 饰
                        </p>
                      </div>
                    </div>
                    <span className="mt-3 w-fit rounded bg-secondary-100 px-2 py-0.5 font-label text-[10px] text-secondary-900">
                      {character.role}
                    </span>
                    <p className="mt-2.5 text-xs leading-relaxed text-foreground-600">
                      {character.desc}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-quill-pen-line text-base"></i>
                </span>
                剧情解析
              </h2>
              <div className="mt-3 rounded-lg border border-background-200/70 bg-background-100/70 p-4 md:p-5">
                <p className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  {drama.analysis}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {drama.types.map((type) => (
                    <span
                      key={type}
                      className="rounded-full bg-accent-500/12 px-2.5 py-1 font-label text-[11px] text-accent-600"
                    >
                      #{type}叙事
                    </span>
                  ))}
                  <span className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900">
                    #导演 {drama.director}
                  </span>
                </div>
              </div>
            </section>

            <section>
              <h2 className="flex items-center gap-2 font-heading text-xl font-semibold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-play-circle-line text-base"></i>
                </span>
                剧集预览演示
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                以下区域为前端播放器界面演示，不含完整剧集内容，也不会提供下载入口。
              </p>
              <div className="mt-3 space-y-4">
                <VideoPreview drama={drama} chapter={currentChapter ?? drama.chapters[0]} />
                <EpisodeList
                  chapters={drama.chapters}
                  activeIndex={activeChapter}
                  onSelect={setActiveChapter}
                />
              </div>
            </section>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-4 md:p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">剧集资料</h2>
              <dl className="mt-3 space-y-2.5 font-label text-xs">
                {[
                  { label: "导演", value: drama.director },
                  { label: "主演", value: drama.cast.join(" / ") },
                  { label: "类型", value: drama.types.join(" / ") },
                  { label: "地区", value: drama.region },
                  { label: "首播年份", value: String(drama.year) },
                  { label: "集数", value: `${drama.episodeCount} 集` },
                  { label: "单集时长", value: drama.duration },
                  { label: "更新状态", value: drama.status },
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-4 border-b border-background-200/50 pb-2.5 last:border-b-0 last:pb-0">
                    <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                    <dd className="text-right text-foreground-800">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-lg border border-primary-500/25 bg-primary-50 p-4 md:p-5">
              <p className="font-heading text-sm font-semibold text-foreground-950">你的收藏</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-700">
                收藏状态只保存在当前浏览器本地。换设备或清除浏览器数据后会丢失，但不会上传到任何服务器。
              </p>
              <button
                type="button"
                onClick={handleFavorite}
                className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-accent-500/50 py-2 font-label text-xs text-accent-600 transition-colors hover:bg-accent-500 hover:text-background-50"
              >
                <i className={`${favorited ? "ri-heart-fill" : "ri-heart-line"} text-sm`}></i>
                {favorited ? "取消收藏" : "加入收藏"}
              </button>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100/70 p-4">
              <h2 className="font-heading text-base font-semibold text-foreground-950">同类推荐</h2>
              <ul className="mt-3 space-y-3">
                {related.slice(0, 3).map((item) => (
                  <li key={item.id}>
                    <Link to={`/detail/${item.id}`} className="group flex cursor-pointer items-center gap-3">
                      <div className="h-16 w-11 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 剧集封面`}
                          title={`${item.title} 剧情介绍`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground-900 transition-colors group-hover:text-primary-500">
                          {item.title}
                        </p>
                        <p className="mt-0.5 font-label text-[11px] text-foreground-500">
                          {item.year} · {item.types[0]}
                        </p>
                      </div>
                      <i className="ri-arrow-right-s-line text-base text-foreground-500"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/category"
                className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300/60 py-2.5 font-label text-xs text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-500"
              >
                <i className="ri-arrow-left-line text-sm"></i>
                返回分类
              </Link>
              <Link
                to="/"
                className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-md border border-background-300/60 py-2.5 font-label text-xs text-foreground-700 transition-colors hover:border-accent-500/60 hover:text-accent-600"
              >
                回到首页
                <i className="ri-home-4-line text-sm"></i>
              </Link>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="border-t border-background-200/60 bg-background-100/40">
            <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-6 md:py-16">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="font-label text-[11px] uppercase tracking-[0.28em] text-primary-500">
                    相关推荐
                  </p>
                  <h2 className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                    看完这部，你可能会喜欢
                  </h2>
                </div>
                <Link
                  to="/category"
                  className="cursor-pointer font-label text-xs text-foreground-600 transition-colors hover:text-accent-500"
                >
                  浏览更多剧集
                  <i className="ri-arrow-right-line ml-1 text-sm"></i>
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-7 md:grid-cols-4 md:gap-x-6">
                {related.map((item) => (
                  <DramaCard key={item.id} drama={item} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <SiteFooter />
      {toast && <Toast message={toast} onClose={closeToast} />}
    </div>
  );
}